import type * as React from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from "./ui/alert-dialog";
import { buttonVariants } from "./ui/button";
import { useI18n } from "../i18n";

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  onConfirm: () => void;
  onCancel?: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
  /** The confirm button is shown but cannot be pressed (e.g. while what it does is still being counted). */
  confirmDisabled?: boolean;
  /** Extra content between the text and the buttons (a warning, a choice). */
  children?: React.ReactNode;
  /** `danger`: the action deletes or wipes something — the confirm button is solid red (modals brief 28.10). */
  tone?: 'default' | 'danger';
  /** The type of the thing asked about (an entity icon), shown before the title. */
  icon?: React.ReactNode;
}

export default function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  onConfirm,
  onCancel,
  confirmLabel,
  cancelLabel,
  confirmDisabled,
  children,
  tone = 'default',
  icon,
}: ConfirmDialogProps) {
  const { t } = useI18n();
  const resolvedConfirmLabel = confirmLabel ?? t('confirmDialog.confirm');
  const resolvedCancelLabel = cancelLabel ?? t('confirmDialog.cancel');

  const handleCancel = () => {
    onCancel?.();
    onOpenChange(false);
  };

  const handleConfirm = () => {
    onConfirm();
    onOpenChange(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className={icon !== undefined ? "flex items-center gap-2" : undefined}>
            {icon !== undefined && <span className="flex size-[18px] shrink-0 items-center justify-center text-muted-foreground [&>svg]:size-[14px]" aria-hidden="true">{icon}</span>}
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        {children}
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleCancel} data-testid="confirm-dialog-cancel">
            {resolvedCancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={confirmDisabled}
            className={tone === 'danger' ? buttonVariants({ variant: 'destructive' }) : undefined}
            data-tone={tone}
            data-testid="confirm-dialog-confirm"
          >
            {resolvedConfirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
